/**
 * Fetcher for Screening Backend API
 *
 * This fetcher is specifically configured for the screening backend,
 * routing through the proxy-screening endpoint to handle auth and CORS.
 */

import { GenericV1ErrorsArray } from '@type/v1-api-types';


interface FetcherScreeningConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: BodyInit | object;
  headers?: Record<string, string>;
  timeout?: number;
}

interface FetcherScreeningResponse<T> {
  data: T | undefined;
  error: GenericV1ErrorsArray | undefined;
}

/**
 * Fetcher function for Screening Backend API
 * Routes through the proxy-screening endpoint to handle auth and CORS
 */
export const fetcherScreening = async <T>(
  url: string,
  config: FetcherScreeningConfig = {},
): Promise<FetcherScreeningResponse<T>> => {
  const { method = 'GET', body, headers = {}, timeout = 30000 } = config;

  // Use the proxy route for screening API calls
  const proxyUrl = `/api/proxy-screening${url}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    signal: controller.signal,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  // Handle body
  if (body) {
    if (body instanceof FormData) {
      // Don't set Content-Type for FormData, let browser set it
      delete (fetchOptions.headers as Record<string, string>)['Content-Type'];
      fetchOptions.body = body;
    } else if (typeof body === 'object') {
      fetchOptions.body = JSON.stringify(body);
    } else {
      fetchOptions.body = body;
    }
  }

  try {
    const response = await fetch(proxyUrl, fetchOptions);
    clearTimeout(timeoutId);

    if (!response.ok) {
      // Try to parse error response
      try {
        const errorData = await response.json();
        return {
          data: undefined,
          error: {
            errors: [{ code: response.status, message: errorData.message || response.statusText }],
          },
        };
      } catch {
        return {
          data: undefined,
          error: {
            errors: [{ code: response.status, message: response.statusText }],
          },
        };
      }
    }

    // Handle empty responses (201 No Content, etc.)
    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return { data: undefined, error: undefined };
    }

    const data = await response.json() as T;
    return { data, error: undefined };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      return {
        data: undefined,
        error: {
          errors: [{ code: 408, message: 'Request timeout' }],
        },
      };
    }

    return {
      data: undefined,
      error: {
        errors: [{ code: 500, message: (error as Error).message || 'Unknown error' }],
      },
    };
  }
};

export default fetcherScreening;
